<template>
  <a
    :href="banner.url"
    class="relative block size-full overflow-hidden transition-transform duration-300 ease-in-out hover:scale-[1.02]"
    @click.prevent="() => trackPromotionalBannerClick.mutate()"
  >
    <div
      v-if="!isMobile"
      class="relative hidden size-full md:block"
      :class="{ 'h-[360px]': !isDesktopLoaded }"
    >
      <canvas
        v-if="banner.desktopImage?.metadata?.blurhash && !isDesktopLoaded"
        ref="desktopCanvasRef"
        class="size-full object-cover"
        :class="{ 'opacity-0': isDesktopLoaded }"
      />
      <picture
        v-if="true"
        class="relative"
      >
        <source
          :srcset="banner.desktopImage?.derivatives?.desktopWebp?.url"
          type="image/webp"
        />
        <img
          :src="banner.desktopImage?.derivatives?.desktopJpg?.url"
          :alt="banner.altText || banner.name"
          class="size-full object-cover transition-opacity duration-500 ease-in-out"
          :class="{ 'opacity-0': !isDesktopLoaded }"
          loading="lazy"
          @load="handleDesktopLoad"
          @error="handleDesktopError"
        />
      </picture>
    </div>

    <div
      v-else
      class="relative block size-full md:hidden"
      :class="{ 'h-[600px]': !isMobileLoaded }"
    >
      <canvas
        v-if="banner.mobileImage?.metadata?.blurhash && !isMobileLoaded"
        ref="mobileCanvasRef"
        class="size-full object-cover"
        :class="{ 'opacity-0': isMobileLoaded }"
      />

      <picture class="relative">
        <source
          :srcset="banner.mobileImage?.derivatives?.mobileWebp?.url"
          type="image/webp"
        />
        <img
          :src="banner.mobileImage?.derivatives?.mobileJpg?.url"
          :alt="banner.altText || banner.name"
          class="aspect-[4/3] size-full object-cover transition-opacity duration-500 ease-in-out"
          :class="{ 'opacity-0': !isMobileLoaded }"
          loading="lazy"
          @load="handleMobileLoad"
          @error="handleMobileError"
        />
      </picture>
    </div>

    <div class="absolute inset-0 bg-black/0 transition-colors duration-300 ease-in-out hover:bg-black/10" />
  </a>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, inject, type Ref } from 'vue';
import { decode } from 'blurhash';
import { useMutation } from 'vue-query';
import promotionalBannersApi from 'api/promotional-banners';

interface PromotionalBannerProps {
  banner: PromotionalBanner;
  targetPage: string;
}

const props = defineProps<PromotionalBannerProps>();
const emit = defineEmits<{
  click: [banner: PromotionalBanner];
}>();

const isMobile = inject('isMobile') as Ref<boolean>;
const desktopCanvasRef = ref<HTMLCanvasElement>();
const mobileCanvasRef = ref<HTMLCanvasElement>();
const isDesktopLoaded = ref(false);
const isMobileLoaded = ref(false);

function drawBlurhash(canvas: HTMLCanvasElement, blurhash: string, width: number, height: number) {
  try {
    const pixels = decode(blurhash, width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = width;
    canvas.height = height;

    const imageData = ctx.createImageData(width, height);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);
  } catch (error) {
    console.warn('Failed to decode blurhash:', error);
  }
}

function handleDesktopLoad() {
  isDesktopLoaded.value = true;
}

function handleDesktopError() {
  console.warn('Failed to load desktop banner image');
  isDesktopLoaded.value = true; // Hide blurhash even on error
}

function handleMobileLoad() {
  isMobileLoaded.value = true;
}

function handleMobileError() {
  console.warn('Failed to load mobile banner image');
  isMobileLoaded.value = true; // Hide blurhash even on error
}

const trackPromotionalBannerClick = useMutation(
  () => {
    return promotionalBannersApi.trackClick(props.banner.id, props.targetPage);
  },
  {
    onSuccess: () => {
      emit('click', props.banner);
      window.location.href = props.banner.url;
    },
  },
);

onMounted(async () => {
  await nextTick();

  // Draw desktop blurhash
  if (desktopCanvasRef.value && props.banner.desktopImage?.metadata?.blurhash) {
    drawBlurhash(desktopCanvasRef.value, props.banner.desktopImage.metadata.blurhash, 64, 16);
  }

  // Draw mobile blurhash
  if (mobileCanvasRef.value && props.banner.mobileImage?.metadata?.blurhash) {
    drawBlurhash(mobileCanvasRef.value, props.banner.mobileImage.metadata.blurhash, 64, 48);
  }
});
</script>
